import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux.hooks';
import {
  fetchTenant,
  fetchTenantTheme,
  selectTenant,
  selectTenantLoading,
  selectTenantError,
  selectTenantIsInitialized,
  selectTenantTheme,
  clearTenant,
  clearError,
} from '../store/slices/tenant.slice';
import { extractHostname } from '../helpers';

const GARM_SLUG = 'garm';
const GARM_DOMAIN = '.garmisland.com';

const getTenantSlug = (url: string): string | undefined => {
  const { subdomain, hostNoPort } = extractHostname(url);

  // Special case: admin.garmisland.com should use 'garm' as the tenant slug
  if (subdomain === 'admin' && hostNoPort.toLowerCase().endsWith(GARM_DOMAIN)) {
    return GARM_SLUG;
  }

  return subdomain;
};

export const useTenant = () => {
  const dispatch = useAppDispatch();

  const tenant = useAppSelector(selectTenant);
  const loading = useAppSelector(selectTenantLoading);
  const error = useAppSelector(selectTenantError);
  const isInitialized = useAppSelector(selectTenantIsInitialized);
  const theme = useAppSelector(selectTenantTheme);

  useEffect(() => {
    const initializeTenant = async () => {
      if (isInitialized) return;

      const tenantSlug = getTenantSlug(window.location.href);
      dispatch(fetchTenant(tenantSlug));
    };

    initializeTenant();
  }, [dispatch, isInitialized]);

  useEffect(() => {
    if (tenant && !theme) {
      dispatch(fetchTenantTheme());
    }
  }, [dispatch, tenant, theme]);

  const refetchTenant = (slug: string) => {
    const tenantSlug = getTenantSlug(window.location.href);
    dispatch(fetchTenant(tenantSlug));
  };

  const clearTenantData = () => {
    dispatch(clearTenant());
  };

  const clearTenantError = () => {
    dispatch(clearError());
  };

  const refreshTenant = () => {
    const tenantSlug = getTenantSlug(window.location.href);
    dispatch(fetchTenant(tenantSlug));
  };

  return {
    tenant,
    loading,
    error,
    isInitialized,
    theme,
    logoUrl: theme?.assets?.logoUrl || null,

    tenantId: tenant?.id || null,
    tenantSlug: tenant?.slug || null,
    tenantName: tenant?.name || null,
    baseCurrency: tenant?.baseCurrency || null,
    hasSubdomain: !!extractHostname(window.location.href),

    refetchTenant,
    clearTenantData,
    clearTenantError,
    refreshTenant,
  };
};
